import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  aiAnalysisSocketService,
  AiResponseStarted,
  AiResponseDelta,
  AiResponseWidget,
  AiResponseComplete,
  AiResponseError,
  AiCardApplicantFound,
  AiCardReportFound,
  AiCardNoReport,
  AiCardOwnerPicker,
  AiCardAgentLoading,
  AiCardErrorTerminal,
  AiSystemMessage,
} from '../sockets/aiAnalysisSocketService';
import useAuth from './useAuth';
import { ChatCard } from '../components/AiChat/types';

export interface ChatSession {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

export type TurnState = 'idle' | 'streaming' | 'awaiting_action';

export interface UseAiChatReturn {
  cards: ChatCard[];
  sessions: ChatSession[];
  activeSessionId: string | undefined;
  isConnected: boolean;
  isStreaming: boolean;
  agentOnline: boolean;
  turnState: TurnState;
  sendMessage: (text: string) => void;
  cancelResponse: () => void;
  clearMessages: () => void;
  loadSessions: () => void;
  loadSession: (sessionId: string) => void;
  deleteSession: (sessionId: string) => void;
  newSession: (options?: { fresh?: boolean }) => Promise<string | undefined>;
  pickOwner: (cardId: string, applicationNumber: number, ownerIndex: number) => void;
  dismissNoReport: (cardId: string) => void;
  retryNoReport: (cardId: string, applicationNumber: number, ownerIndex: number) => void;
  dismissErrorCard: (cardId: string) => void;
  retryCommand: (cardId: string, command: string) => void;
  endTurn: () => void;
  retry: (cardId: string, lastUserMessage: string) => void;
}

const newId = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const nowIso = () => new Date().toISOString();

interface ErrorSpec {
  text: string;
  retryable: boolean;
  offlineSignal: boolean;
  contactSupport?: boolean;
}

const ERROR_SPECS: Record<string, ErrorSpec> = {
  GATEWAY_UNREACHABLE: {
    text: "Can't reach the AI agent — it looks offline or is restarting. Please try again in a minute.",
    retryable: false,
    offlineSignal: true,
  },
  GATEWAY_AUTH_FAILED: {
    text: 'AI agent rejected our credentials. This is a configuration issue — please contact support.',
    retryable: false,
    offlineSignal: false,
    contactSupport: true,
  },
  GATEWAY_BAD_REQUEST: {
    text: 'AI agent rejected the request payload. Please contact support if this persists.',
    retryable: false,
    offlineSignal: false,
    contactSupport: true,
  },
  GATEWAY_SERVER_ERROR: {
    text: 'AI agent reported an internal error. Try again in a moment; if it persists, contact support.',
    retryable: true,
    offlineSignal: true,
  },
  GATEWAY_EMPTY_BODY: {
    text: 'The AI agent returned no data. Please try again.',
    retryable: true,
    offlineSignal: false,
  },
  STREAM_ABORTED: {
    text: 'Request was cancelled.',
    retryable: true,
    offlineSignal: false,
  },
  STREAM_STALLED: {
    text: 'The AI agent stopped responding mid-stream. The partial answer above is preserved — you can retry to continue.',
    retryable: true,
    offlineSignal: true,
  },
  STREAM_TIMEOUT: {
    text: 'The analysis took too long (over 10 minutes) and was aborted. Try again or rephrase the request.',
    retryable: true,
    offlineSignal: false,
  },
  STREAM_FAILED: {
    text: 'The connection to the AI agent dropped mid-stream. Please retry.',
    retryable: true,
    offlineSignal: true,
  },
  CLIENT_WATCHDOG_TIMEOUT: {
    text: 'No response received in time. The agent may have lost the connection — please retry.',
    retryable: true,
    offlineSignal: true,
  },
  MALFORMED_WIDGETS: {
    text: 'The AI produced malformed output at the end; the text above is preserved.',
    retryable: false,
    offlineSignal: false,
  },
  PERSIST_FAILED: {
    text: "Response generated but couldn't be saved. Retrying will re-run the analysis.",
    retryable: true,
    offlineSignal: false,
  },
  HISTORY_FAILED: {
    text: "Couldn't load conversation history.",
    retryable: true,
    offlineSignal: false,
  },
  TRANSFORM_FAILED: {
    text: "The credit report couldn't be prepared for analysis. Please re-pull the report and try again.",
    retryable: false,
    offlineSignal: false,
  },
  RATE_LIMITED: {
    text: 'Too many requests in a short window. Please wait a moment before retrying.',
    retryable: true,
    offlineSignal: false,
  },
  BUDGET_EXCEEDED: {
    text: 'Daily AI spend cap reached for this tenant. Contact your admin to raise the limit.',
    retryable: false,
    offlineSignal: false,
    contactSupport: true,
  },
};

const WATCHDOG_TIMEOUT_MS = 320_000;

function resolveErrorSpec(code: string | undefined, fallback: string | undefined): ErrorSpec & { displayText: string } {
  const spec = code ? ERROR_SPECS[code] : undefined;
  if (spec) return { ...spec, displayText: spec.text };
  return {
    text: fallback ?? 'Unexpected AI agent error.',
    retryable: true,
    offlineSignal: false,
    displayText: fallback ?? 'Unexpected AI agent error.',
  };
}

function isAwaitingActionCard(card: ChatCard): boolean {
  if (card.type === 'no_report_card') {
    if (card.resolved) return false;
    return card.state === 'ready' || card.state === 'gated_pii';
  }
  if (card.type === 'owner_picker_card') {
    return !card.resolved;
  }
  if (card.type === 'error_card') {
    if (card.resolved) return false;
    const actions = card.actions ?? [];
    return actions.some((a) => a.kind === 'open_application' || a.kind === 'retry');
  }
  return false;
}

function deriveTurnState(cards: ChatCard[], isStreaming: boolean): TurnState {
  if (isStreaming) return 'streaming';
  for (let i = cards.length - 1; i >= 0; i--) {
    const c = cards[i];
    if (c.type === 'user_message') return 'idle';
    if (isAwaitingActionCard(c)) return 'awaiting_action';
  }
  return 'idle';
}

// Reconstruct ChatCard from a persisted Report Analysis card-event shape.
// Backend persists Report Analysis flow cards (applicant/report/no-report/owner-picker/
// system_message/error_card) inside ai_chat_message.widgets[] as objects
// shaped { cardId, event, payload }. On reload we need to convert each one
// back to its original ChatCard variant so the UI renders the full Report Analysis
// timeline, not a single empty analysis_card.
function reportAnalysisCardEventToChatCard(evt: any, ts: string): ChatCard | null {
  if (!evt || typeof evt !== 'object') return null;
  const id = evt.cardId || newId('card');
  const p = evt.payload || {};
  switch (evt.event) {
    case 'ai:system:message':
      return { type: 'system_message', id, ts, text: p.text, spinner: p.spinner };
    case 'ai:card:applicant_found':
      return { type: 'applicant_card', id, ts, data: p.data };
    case 'ai:card:report_found':
      return { type: 'report_card', id, ts, data: p.data };
    case 'ai:card:no_report':
      return {
        type: 'no_report_card',
        id, ts,
        applicationId: p.applicationId,
        applicationNumber: p.applicationNumber,
        ownerIndex: p.ownerIndex,
        ownerName: p.ownerName,
        state: p.state,
        missingFields: p.missingFields,
        resolved: true,  // historical card: action no longer actionable
      };
    case 'ai:card:owner_picker':
      return {
        type: 'owner_picker_card',
        id, ts,
        applicationId: p.applicationId,
        applicationNumber: p.applicationNumber,
        rawNeedle: p.rawNeedle,
        candidates: p.candidates ?? [],
        reason: p.reason,
        resolved: true,
      };
    case 'ai:card:agent_loading':
      return null;  // transient spinner — never persisted as final state
    case 'ai:card:error':
      return {
        type: 'error_card',
        id, ts,
        text: p.text,
        severity: p.severity,
        applicationId: p.applicationId,
        applicationNumber: p.applicationNumber,
        actions: p.actions,
        resolved: true,
      };
    default:
      return null;
  }
}

// Tell apart Report Analysis persisted card-events from agent-emitted widgets
// (score_card / risk_summary / etc). Report Analysis events carry { cardId, event,
// payload }; agent widgets carry { type, data }.
function isReportAnalysisCardEvent(w: any): boolean {
  return !!w && typeof w === 'object' && typeof w.event === 'string' && typeof w.cardId === 'string';
}

type StreamSegment =
  | { kind: 'text'; id: string; text: string }
  | { kind: 'widget'; id: string; widget: any };

/**
 * Build interleaved segments from a persisted assistant message. The agent
 * streams position markers inline with prose; this splits the text on
 * markers and weaves widgets back into the order the agent emitted them,
 * so PDF and history-reload both render in the right sequence rather than
 * dumping all widgets at the end.
 */
function buildSegmentsFromAgentStream(text: string, widgets: any[]): StreamSegment[] {
  const out: StreamSegment[] = [];
  if (!text) {
    for (const w of widgets) out.push({ kind: 'widget', id: newId('seg'), widget: w });
    return out;
  }
  const markerRe = /\[\[W:(\d+)\]\]/g;
  let lastIdx = 0;
  const referenced = new Set<number>();
  let m: RegExpExecArray | null;
  while ((m = markerRe.exec(text)) !== null) {
    const before = text.slice(lastIdx, m.index);
    if (before.trim().length > 0) {
      out.push({ kind: 'text', id: newId('seg'), text: before });
    }
    const idx = parseInt(m[1], 10);
    if (Number.isFinite(idx) && widgets[idx]) {
      out.push({ kind: 'widget', id: newId('seg'), widget: widgets[idx] });
      referenced.add(idx);
    }
    lastIdx = m.index + m[0].length;
  }
  const tail = text.slice(lastIdx);
  if (tail.trim().length > 0) {
    out.push({ kind: 'text', id: newId('seg'), text: tail });
  }
  for (let i = 0; i < widgets.length; i++) {
    if (!referenced.has(i)) out.push({ kind: 'widget', id: newId('seg'), widget: widgets[i] });
  }
  return out;
}

function messagesToCards(messages: any[], sessionId?: string): ChatCard[] {
  const out: ChatCard[] = [];
  for (const m of messages || []) {
    const ts = m.createdAt || nowIso();
    if (m.role === 'user') {
      out.push({ type: 'user_message', id: m.id || newId('user'), ts, text: m.text || '' });
      continue;
    }
    // assistant
    const widgets: any[] = Array.isArray(m.widgets) ? m.widgets : [];
    if (widgets.length > 0 && widgets.every(isReportAnalysisCardEvent)) {
      // pure Report Analysis persisted-cards row → expand into individual ChatCards
      for (const w of widgets) {
        const card = reportAnalysisCardEventToChatCard(w, ts);
        if (card) out.push(card);
      }
      continue;
    }
    // analysis row (or empty assistant message we want to render)
    const analysisWidgets = widgets.filter((w: any) => !isReportAnalysisCardEvent(w));
    const rawText = m.text || '';
    out.push({
      type: 'analysis_card',
      id: m.id || newId('analysis'),
      ts,
      // Visible text in card.text excludes position markers; the segment
      // builder uses the original raw text to interleave widgets at the
      // right place.
      text: rawText.replace(/\[\[W:\d+\]\]/g, ''),
      widgets: analysisWidgets,
      segments: buildSegmentsFromAgentStream(rawText, analysisWidgets),
      messageId: m.id,
      sessionId,
    });
  }
  return out;
}

export function useAiChat(): UseAiChatReturn {
  const [cards, setCards] = useState<ChatCard[]>([]);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [agentOnline, setAgentOnline] = useState(true);
  const { user } = useAuth();

  const sessionIdRef = useRef<string | undefined>();
  const activeRequestIdRef = useRef<string | undefined>();
  const activeAnalysisCardIdRef = useRef<string | undefined>();
  const lastUserMessageRef = useRef<string | undefined>();
  const connectedRef = useRef(false);
  const watchdogTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>();
  const onWatchdogFireRef = useRef<(() => void) | undefined>();

  const clearWatchdog = useCallback(() => {
    if (watchdogTimerRef.current) {
      clearTimeout(watchdogTimerRef.current);
      watchdogTimerRef.current = undefined;
    }
  }, []);

  const armWatchdog = useCallback(() => {
    clearWatchdog();
    watchdogTimerRef.current = setTimeout(() => {
      onWatchdogFireRef.current?.();
    }, WATCHDOG_TIMEOUT_MS);
  }, [clearWatchdog]);

  const turnState = useMemo<TurnState>(() => deriveTurnState(cards, isStreaming), [cards, isStreaming]);

  const replaceCard = useCallback((cardId: string, updater: (prev: ChatCard) => ChatCard) => {
    setCards(prev => prev.map(c => (c.id === cardId ? updater(c) : c)));
  }, []);

  const dropAgentThinking = useCallback(() => {
    setCards(prev => prev.filter(c => c.type !== 'agent_thinking'));
  }, []);

  onWatchdogFireRef.current = () => {
    if (!isStreaming) return;
    clearWatchdog();
    setIsStreaming(false);
    if (activeRequestIdRef.current) {
      aiAnalysisSocketService.cancelRequest(activeRequestIdRef.current);
      activeRequestIdRef.current = undefined;
    }
    dropAgentThinking();
    const id = activeAnalysisCardIdRef.current;
    const spec = ERROR_SPECS.CLIENT_WATCHDOG_TIMEOUT;
    if (spec.offlineSignal) setAgentOnline(false);
    if (id) {
      setCards(prev => prev.map(c =>
        c.id === id && c.type === 'analysis_card'
          ? { ...c, isStreaming: false, error: spec.text }
          : c,
      ));
      activeAnalysisCardIdRef.current = undefined;
    } else {
      setCards(prev => [
        ...prev,
        {
          type: 'agent_error',
          id: newId('agentErr'),
          ts: nowIso(),
          error: spec.text,
          code: 'CLIENT_WATCHDOG_TIMEOUT',
          retryable: spec.retryable,
          lastUserMessage: spec.retryable ? lastUserMessageRef.current : undefined,
        },
      ]);
    }
  };

  const resolvePriorAwaitingCards = useCallback(() => {
    setCards(prev =>
      prev.map(c => {
        if (!isAwaitingActionCard(c)) return c;
        if (c.type === 'no_report_card') return { ...c, resolved: true };
        if (c.type === 'owner_picker_card') return { ...c, resolved: true };
        if (c.type === 'error_card') return { ...c, resolved: true };
        return c;
      }),
    );
  }, []);

  useEffect(() => {
    if (!user?.id || connectedRef.current) return;

    const userId = user.id;
    const config = { namespace: 'ai-analysis', userId };

    aiAnalysisSocketService
      .connect(config, {
        onStarted: (data: AiResponseStarted) => {
          activeRequestIdRef.current = data.requestId;
          if (data.sessionId) sessionIdRef.current = data.sessionId;
          dropAgentThinking();
          setIsStreaming(true);
          armWatchdog();
        },

        onDelta: (data: AiResponseDelta) => {
          dropAgentThinking();
          setAgentOnline(true);
          armWatchdog();
          // The agent's stream embeds widget-position markers like `[[W:0]]`
          // in the text. They are essential for PDF and history-reload to
          // place widgets back at the right spot, but the live chat UI gets
          // widget segments from the explicit `widget` event, so the marker
          // is just noise here — strip it from the visible/segment text.
          // We still keep the original chunk in `c.text` (the persisted
          // backbone) so reload can re-interleave correctly.
          const visibleText = data.text.replace(/\[\[W:\d+\]\]/g, '');
          let id = activeAnalysisCardIdRef.current;
          if (!id) {
            id = newId('analysis');
            activeAnalysisCardIdRef.current = id;
            setCards(prev => [
              ...prev,
              {
                type: 'analysis_card',
                id: id!,
                ts: nowIso(),
                text: '',
                widgets: [],
                segments: [],
                isStreaming: true,
              },
            ]);
          }
          replaceCard(id, c => {
            if (c.type !== 'analysis_card') return c;
            const segments = c.segments ?? [];
            const last = segments[segments.length - 1];
            const nextSegments = !visibleText
              ? segments
              : last && last.kind === 'text'
                ? [...segments.slice(0, -1), { ...last, text: last.text + visibleText }]
                : [...segments, { kind: 'text' as const, id: newId('seg'), text: visibleText }];
            return { ...c, text: c.text + data.text, segments: nextSegments };
          });
        },

        onWidget: (data: AiResponseWidget) => {
          dropAgentThinking();
          armWatchdog();
          let id = activeAnalysisCardIdRef.current;
          if (!id) {
            id = newId('analysis');
            activeAnalysisCardIdRef.current = id;
            setCards(prev => [
              ...prev,
              {
                type: 'analysis_card',
                id: id!,
                ts: nowIso(),
                text: '',
                widgets: [],
                segments: [],
                isStreaming: true,
              },
            ]);
          }
          replaceCard(id, c => {
            if (c.type !== 'analysis_card') return c;
            const segments = c.segments ?? [];
            return {
              ...c,
              widgets: [...c.widgets, data.widget],
              segments: [...segments, { kind: 'widget', id: newId('seg'), widget: data.widget }],
            };
          });
        },

        onComplete: (data: AiResponseComplete) => {
          clearWatchdog();
          sessionIdRef.current = data.sessionId;
          activeRequestIdRef.current = undefined;
          setIsStreaming(false);
          setAgentOnline(true);
          dropAgentThinking();
          const id = activeAnalysisCardIdRef.current;
          if (id) {
            replaceCard(id, c =>
              c.type === 'analysis_card'
                ? {
                    ...c,
                    isStreaming: false,
                    messageId: data.messageId ?? c.messageId,
                    sessionId: data.sessionId ?? c.sessionId,
                  }
                : c,
            );
          }
          activeAnalysisCardIdRef.current = undefined;
        },

        onError: (data: AiResponseError) => {
          // Widget sentinel JSON failed to parse — text was already streamed
          // and the user has the agent's response. Don't surface a banner;
          // just log. (The backend stops emitting this code as of 2026-05-05;
          // the guard remains for older NestJS instances and replays.)
          if (data.code === 'MALFORMED_WIDGETS') {
            // eslint-disable-next-line no-console
            console.warn('[AiChat] malformed widget block (silent):', data.message ?? data.error);
            return;
          }
          clearWatchdog();
          setIsStreaming(false);
          activeRequestIdRef.current = undefined;
          dropAgentThinking();
          const spec = resolveErrorSpec(data.code, data.message ?? data.error);
          if (spec.offlineSignal) setAgentOnline(false);
          const id = activeAnalysisCardIdRef.current;
          if (id) {
            replaceCard(id, c =>
              c.type === 'analysis_card' ? { ...c, isStreaming: false, error: spec.displayText } : c,
            );
            activeAnalysisCardIdRef.current = undefined;
          } else {
            setCards(prev => [
              ...prev,
              {
                type: 'agent_error',
                id: newId('agentErr'),
                ts: nowIso(),
                error: spec.displayText,
                code: data.code,
                retryable: spec.retryable,
                lastUserMessage: spec.retryable ? lastUserMessageRef.current : undefined,
              },
            ]);
          }
        },

        onApplicantFound: (data: AiCardApplicantFound) => {
          setCards(prev => [
            ...prev,
            { type: 'applicant_card', id: data.cardId || newId('applicant'), ts: nowIso(), data: data.data },
          ]);
        },

        onReportFound: (data: AiCardReportFound) => {
          setCards(prev => [
            ...prev,
            { type: 'report_card', id: data.cardId || newId('report'), ts: nowIso(), data: data.data },
          ]);
        },

        onNoReport: (data: AiCardNoReport) => {
          setCards(prev => [
            ...prev,
            {
              type: 'no_report_card',
              id: data.cardId || newId('noreport'),
              ts: nowIso(),
              applicationId: data.applicationId,
              applicationNumber: data.applicationNumber,
              ownerIndex: data.ownerIndex,
              ownerName: data.ownerName,
              state: data.state,
              missingFields: data.missingFields,
            },
          ]);
        },

        onOwnerPicker: (data: AiCardOwnerPicker) => {
          setCards(prev => [
            ...prev,
            {
              type: 'owner_picker_card',
              id: data.cardId || newId('picker'),
              ts: nowIso(),
              applicationId: data.applicationId,
              applicationNumber: data.applicationNumber,
              rawNeedle: data.rawNeedle,
              candidates: data.candidates ?? [],
              reason: data.reason ?? 'ambiguous',
            },
          ]);
        },

        onAgentLoading: (data: AiCardAgentLoading) => {
          setCards(prev => [
            ...prev,
            { type: 'agent_thinking', id: data.cardId || newId('thinking'), ts: nowIso() },
          ]);
        },

        onErrorCard: (data: AiCardErrorTerminal) => {
          setCards(prev => [
            ...prev,
            {
              type: 'error_card',
              id: data.cardId || newId('err'),
              ts: nowIso(),
              text: data.text,
              severity: data.severity,
              applicationId: data.applicationId,
              applicationNumber: data.applicationNumber,
              actions: data.actions,
            },
          ]);
        },

        onSystemMessage: (data: AiSystemMessage) => {
          setCards(prev => [
            ...prev,
            { type: 'system_message', id: data.cardId || newId('sys'), ts: nowIso(), text: data.text, spinner: data.spinner },
          ]);
        },

        onResolvePending: () => {
          resolvePriorAwaitingCards();
        },
      })
      .then(() => {
        setIsConnected(true);
        connectedRef.current = true;
        aiAnalysisSocketService.getCurrentSession((current) => {
          if (!current.sessionId) return;
          sessionIdRef.current = current.sessionId;
          aiAnalysisSocketService.loadSession(current.sessionId, (data) => {
            if (Array.isArray(data.cards) && data.cards.length > 0) {
              setCards(data.cards);
            } else if (Array.isArray(data.messages) && data.messages.length > 0) {
              setCards(messagesToCards(data.messages, current.sessionId));
            }

            if (current.activeRequestId) {
              activeRequestIdRef.current = current.activeRequestId;
              setIsStreaming(true);
              setAgentOnline(true);
              armWatchdog();
              setCards(prev => {
                const alreadyHasThinking = prev.some(c => c.type === 'agent_thinking');
                if (alreadyHasThinking) return prev;
                return [
                  ...prev,
                  { type: 'agent_thinking', id: newId('thinking'), ts: nowIso() },
                ];
              });
            }
          });
        });
      })
      .catch(() => {
        setIsConnected(false);
      });

    return () => {
      aiAnalysisSocketService.disconnect();
      connectedRef.current = false;
      setIsConnected(false);
    };
  }, [user?.id, replaceCard, dropAgentThinking, resolvePriorAwaitingCards]);

  const sendMessage = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isStreaming) return;
    if (turnState === 'awaiting_action') {
      resolvePriorAwaitingCards();
    }
    lastUserMessageRef.current = trimmed;
    setCards(prev => [
      ...prev,
      { type: 'user_message', id: newId('user'), ts: nowIso(), text: trimmed },
    ]);
    aiAnalysisSocketService.sendMessage(trimmed, sessionIdRef.current);
  }, [isStreaming, turnState, resolvePriorAwaitingCards]);

  const cancelResponse = useCallback(() => {
    if (activeRequestIdRef.current) {
      aiAnalysisSocketService.cancelRequest(activeRequestIdRef.current);
      setIsStreaming(false);
      const id = activeAnalysisCardIdRef.current;
      if (id) {
        replaceCard(id, c =>
          c.type === 'analysis_card'
            ? { ...c, isStreaming: false, text: c.text + '\n\n*[Cancelled]*' }
            : c,
        );
        activeAnalysisCardIdRef.current = undefined;
      }
      activeRequestIdRef.current = undefined;
    }
  }, [replaceCard]);

  const clearMessages = useCallback(() => {
    if (sessionIdRef.current) {
      aiAnalysisSocketService.deleteSession(sessionIdRef.current, () => {
        setSessions(prev => prev.filter(s => s.id !== sessionIdRef.current));
      });
    }
    setCards([]);
    sessionIdRef.current = undefined;
    activeAnalysisCardIdRef.current = undefined;
    lastUserMessageRef.current = undefined;
  }, []);

  const newSession = useCallback(async (options?: { fresh?: boolean }) => {
    setCards([]);
    sessionIdRef.current = undefined;
    activeAnalysisCardIdRef.current = undefined;
    lastUserMessageRef.current = undefined;
    try {
      const created = await aiAnalysisSocketService.startNewSession(options);
      sessionIdRef.current = created.sessionId;
      aiAnalysisSocketService.listSessions(listData => {
        setSessions(listData.sessions || []);
      });
      return created.sessionId;
    } catch {
      return undefined;
    }
  }, []);

  const loadSessions = useCallback(() => {
    aiAnalysisSocketService.listSessions(data => {
      setSessions(data.sessions || []);
    });
  }, []);

  const loadSession = useCallback((sessionId: string) => {
    aiAnalysisSocketService.loadSession(sessionId, data => {
      sessionIdRef.current = sessionId;
      lastUserMessageRef.current = undefined;
      if (Array.isArray(data.cards)) {
        setCards(data.cards);
      } else if (Array.isArray(data.messages)) {
        setCards(messagesToCards(data.messages, sessionId));
      } else {
        setCards([]);
      }
    });
  }, []);

  const deleteSession = useCallback((sessionId: string) => {
    aiAnalysisSocketService.deleteSession(sessionId, () => {
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (sessionIdRef.current === sessionId) {
        setCards([]);
        sessionIdRef.current = undefined;
      }
    });
  }, []);

  const dismissNoReport = useCallback((cardId: string) => {
    replaceCard(cardId, c => (c.type === 'no_report_card' ? { ...c, resolved: true } : c));
    if (sessionIdRef.current) {
      aiAnalysisSocketService.sendAction('end_turn', {
        sessionId: sessionIdRef.current,
        silent: true,
        cardId,
      });
    }
  }, [replaceCard]);

  const retryNoReport = useCallback((cardId: string, applicationNumber: number, ownerIndex: number) => {
    replaceCard(cardId, c => (c.type === 'no_report_card' ? { ...c, resolved: true } : c));
    const command = `Analyze report for application ${applicationNumber} owner ${ownerIndex}`;
    lastUserMessageRef.current = command;
    setCards(prev => [
      ...prev,
      { type: 'user_message', id: newId('user'), ts: nowIso(), text: command },
    ]);
    aiAnalysisSocketService.sendAction('retry_analyze', {
      cardId,
      command,
      sessionId: sessionIdRef.current,
    });
  }, [replaceCard]);

  const pickOwner = useCallback((cardId: string, applicationNumber: number, ownerIndex: number) => {
    replaceCard(cardId, c => (c.type === 'owner_picker_card' ? { ...c, resolved: true } : c));
    const command = `Analyze report for application ${applicationNumber} owner ${ownerIndex}`;
    lastUserMessageRef.current = command;
    setCards(prev => [
      ...prev,
      { type: 'user_message', id: newId('user'), ts: nowIso(), text: command },
    ]);
    aiAnalysisSocketService.sendAction('pick_owner', {
      cardId,
      applicationNumber,
      ownerIndex,
      sessionId: sessionIdRef.current,
    });
  }, [replaceCard]);

  const dismissErrorCard = useCallback((cardId: string) => {
    replaceCard(cardId, c => (c.type === 'error_card' ? { ...c, resolved: true } : c));
    if (sessionIdRef.current) {
      aiAnalysisSocketService.sendAction('end_turn', {
        sessionId: sessionIdRef.current,
        silent: true,
        cardId,
      });
    }
  }, [replaceCard]);

  const retryCommand = useCallback((cardId: string, command: string) => {
    replaceCard(cardId, c => (c.type === 'error_card' ? { ...c, resolved: true } : c));
    lastUserMessageRef.current = command;
    setCards(prev => [
      ...prev,
      { type: 'user_message', id: newId('user'), ts: nowIso(), text: command },
    ]);
    aiAnalysisSocketService.sendAction('retry_analyze', {
      cardId,
      command,
      sessionId: sessionIdRef.current,
    });
  }, [replaceCard]);

  const endTurn = useCallback(() => {
    resolvePriorAwaitingCards();
    if (sessionIdRef.current) {
      aiAnalysisSocketService.sendAction('end_turn', {
        sessionId: sessionIdRef.current,
      });
    }
  }, [resolvePriorAwaitingCards]);

  const retry = useCallback((_cardId: string, lastUserMessage: string) => {
    sendMessage(lastUserMessage);
  }, [sendMessage]);

  return {
    cards,
    sessions,
    activeSessionId: sessionIdRef.current,
    isConnected,
    isStreaming,
    agentOnline,
    turnState,
    sendMessage,
    cancelResponse,
    clearMessages,
    loadSessions,
    loadSession,
    deleteSession,
    newSession,
    pickOwner,
    dismissNoReport,
    retryNoReport,
    dismissErrorCard,
    retryCommand,
    endTurn,
    retry,
  };
}
